import React, { useCallback, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useDispatch } from 'react-redux';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { unwrapResult } from '@reduxjs/toolkit';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import ContextMenu from 'fe-design-base/molecules/ContextMenu';
import SimpleMenuItem from 'fe-design-base/molecules/ContextMenu/SimpleMenuItem';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';
import Pill from 'fe-design-base/molecules/Pill';
import { Form, Formik } from 'formik';

import { updateUserInfo } from 'actions/employeeView';

import { sendPacket } from 'features/employeeOnboarding/documents/slices';
import submitTaxForm from 'features/team/components/TaxEntryForm/actions/submitTaxForm';
import TaxForm from 'features/team/components/TaxEntryForm/Steps/TaxForm';
import {
  initialValues,
  validationSchema,
} from 'features/team/drawers/TaxEntryDrawer/constants';

import * as flashNotice from 'util/flashNotice';
import { toI18n } from 'util/i18n';

import InfoBanner from './InfoBanner';

const WithholdingAccordion = ({ userData, formData }) => {
  const dispatch = useDispatch();
  const user = userData.toJS();
  const form = useMemo(
    () =>
      user.employee_forms.find(
        f => f.description === formData.get('description')
      ),
    [user, formData]
  );

  const [formVisible, setFormVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(!form.is_submitted);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const data = useMemo(
    () =>
      form.parameters.map(item => ({
        label: item.label,
        value: item.decorated_value,
      })),
    [form]
  );

  const handleClickAway = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const handleOnClickAddFormDetails = useCallback(() => {
    handleClickAway();
    setFormVisible(true);
  }, [setFormVisible, handleClickAway]);

  const handleOnClickHideFormDetails = useCallback(() => {
    setFormVisible(false);
  }, [setFormVisible]);

  const handleOnClickSendPacket = useCallback(() => {
    handleClickAway();
    dispatch(
      sendPacket({
        userId: user.id,
      })
    )
      .then(unwrapResult)
      .then(result => {
        if (result.missing_user_data) {
          flashNotice.error(
            toI18n(
              'employee_documents.packet_status.missing_contact_info_notice'
            )
          );
          return;
        } else if (result.no_job_at_current_location) {
          return flashNotice.error(result.errors);
        }
        flashNotice.success(
          toI18n('employee_documents.packet_status.sent_success')
        );
      });
  }, [user, dispatch, handleClickAway]);

  const handleFormSubmit = useCallback(
    values => {
      setIsLoading(true);

      submitTaxForm(form.description, user, values)
        .then(response => {
          if (response) {
            dispatch(updateUserInfo(response)); // update Redux store
          }
          setFormVisible(false);
        })
        .catch(() => null)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [form, user, dispatch]
  );

  const handleIsOpen = useCallback(() => {
    setIsOpen(open => !open);
  }, []);

  const handleClickTrigger = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);
  return (
    <Box>
      <Accordion disableGutters expanded={isOpen} onChange={handleIsOpen}>
        <AccordionSummary
          sx={{
            padding: 0,
            '.MuiAccordionSummary-content': {
              margin: 0,
            },
          }}
        >
          <Box p={16} row w="100%" spacebetween>
            <Box row vcenter>
              <Text variant="heading3">{form.description}</Text>
              <Box ml={12}>
                {form.is_submitted && (
                  <Pill variant="success" icon="Success">
                    {toI18n('team.employee_profile.tax_information.on_file')}
                  </Pill>
                )}

                {!form.is_submitted && (
                  <Box ml={12}>
                    <Pill
                      variant={form.is_blocking ? 'error' : 'warning'}
                      icon={form.is_blocking ? 'Critical' : 'Warning'}
                    >
                      {toI18n('team.employee_profile.tax_information.missing')}
                    </Pill>
                  </Box>
                )}
              </Box>
            </Box>

            <Box vcenter>
              <Box>
                <FontAwesomeIcon
                  color="grayLight"
                  icon={isOpen ? faChevronUp : faChevronDown}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!form.is_submitted && !formVisible && (
            <Box mt={16} mb={24} ml={16} mr={16}>
              <Box>
                <InfoBanner
                  user={userData}
                  form={formData}
                  onClickAddFormDetails={handleOnClickAddFormDetails}
                />
              </Box>
            </Box>
          )}

          {formVisible && (
            <Formik
              validationSchema={validationSchema(user, form.description)}
              initialValues={initialValues(user, form.description)}
              onSubmit={handleFormSubmit}
              enableReinitialize
            >
              {formProps => (
                <Form>
                  <Box maxw={776}>
                    <TaxForm
                      isTeamDrawer={false}
                      parameters={form.parameters}
                    />
                  </Box>

                  <Box rowreverse mt32>
                    <Box row>
                      <Box>
                        <Button
                          variant="tertiary"
                          onClick={handleOnClickHideFormDetails}
                        >
                          <Text i18n="actions.cancel" />
                        </Button>
                      </Box>
                      <Box ml={16}>
                        <Button
                          variant="primary"
                          disabled={!formProps.isValid || !formProps.dirty}
                          isLoading={isLoading}
                          type="submit"
                        >
                          <Text i18n="actions.save" />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          )}
          {!formVisible && form.is_submitted && (
            <>
              {!data && <CircularInfinite size="medium" variant="dark" />}
              {data && (
                <Box grid gridTemplateColumns="1fr 150px">
                  <Box grid gridTemplateColumns="1fr 1fr" grid-gap="4px 4px">
                    {data.map((item, index) => (
                      <Box key={index}>
                        <Box mb={8}>
                          <Text variant="bodySmBold" color="mono">
                            {item.label}
                          </Text>
                        </Box>
                        <Box mb={8}>
                          <Text variant="bodySm" color="mono">
                            {item.value}
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box h={44} hright mr={8}>
                    <Button
                      variant="secondary"
                      endIcon="CaretDown"
                      onClick={handleClickTrigger}
                    >
                      {toI18n(
                        'team.employee_profile.tax_information.accordion.actions'
                      )}
                    </Button>
                    <ContextMenu
                      open={Boolean(anchorEl)}
                      onClose={handleClickAway}
                      anchorEl={anchorEl}
                    >
                      <SimpleMenuItem
                        onClick={handleOnClickAddFormDetails}
                        label={toI18n(
                          'team.employee_profile.tax_information.accordion.update_form_myself'
                        )}
                        iconName="Edit"
                      />

                      <SimpleMenuItem
                        onClick={handleOnClickSendPacket}
                        label={toI18n(
                          'team.employee_profile.tax_information.accordion.send_form'
                        )}
                        iconName="Email"
                      />
                    </ContextMenu>
                  </Box>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

WithholdingAccordion.propTypes = {
  userData: ImmutablePropTypes.map.isRequired,
  formData: ImmutablePropTypes.map.isRequired,
};

export default WithholdingAccordion;
